import { activityName } from '@d-orca/shared/text';
import type { ISchedulerTask } from '@interfaces/IScheduler';

export type NameCollision = 'sibling' | 'committed' | null;

/**
 * Why a task's name needs a warning in the edit form, if at all.
 *
 * - `committed`: another COMMITTED task already carries this task's ACTIVITY
 *   name, so committing this one will be refused — Trusstor keeps one activity
 *   per name in the project's shared sequence. The activity name is the
 *   parent-qualified one (`activityName`, QA F-62) — the same function the
 *   server names the activity with — so repeated per-floor sub-tasks under
 *   differently named parents do not clash. Checked only while this task is
 *   still uncommitted (a committed one already got through).
 * - `sibling`: a task under the same parent in the same group has it — two
 *   identically named rows side by side are confusing. Repeated per-floor
 *   sub-tasks ("Fixtures, doors & handover" under every floor's parent) are
 *   NOT siblings, so they no longer warn on an untouched task.
 */
export function nameCollision(draft: ISchedulerTask, tasks: readonly ISchedulerTask[]): NameCollision {
  const name = draft.name.trim();
  if (name === '') return null;
  const others = tasks.filter((task) => task.id !== draft.id);
  if (!draft.committed) {
    const nameById = new Map(tasks.map((task) => [task.id, task.name] as const));
    const activityNameOf = (task: ISchedulerTask, ownName: string) =>
      activityName(ownName, task.parentId ? nameById.get(task.parentId) : undefined);
    const mine = activityNameOf(draft, name);
    if (others.some((task) => task.committed && activityNameOf(task, task.name) === mine)) return 'committed';
  }
  const sibling = others.some(
    (task) => task.name === name && task.groupId === draft.groupId && (task.parentId ?? null) === (draft.parentId ?? null),
  );
  return sibling ? 'sibling' : null;
}
